import { Box, Button, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useSubmitPaymentLinkData } from "../../../../api/queries/paymentLinks/paymentLinksMutations";
import { useParams } from "react-router-dom";

type Field = {
  title: string;
  required: boolean;
  default?: boolean;
  inserted?: boolean;
  isCustom?: boolean;
};

const createDynamicSchema = (fields: Field[]) => {
  const shape: Record<string, z.ZodTypeAny> = {};

  fields.forEach((field) => {
    const key = field.title.toLowerCase().replace(/\s+/g, "_");
    shape[key] = field.required ? z.string().min(1, "This field is required by the entity") : z.string().optional();
  });

  return z.object(shape);
};

const PaymentLinkForm = ({ fields, link }: { fields: Field[]; link: string }) => {
  const { t } = useTranslation();
  const { linkId } = useParams();
  const schema = createDynamicSchema(fields);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  });

  const onSubmitSuccess = () => (window.location.href = link);

  const { mutate } = useSubmitPaymentLinkData(onSubmitSuccess);

  const onSubmit = (data: Record<string, string>) => mutate({ linkId, data });

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        width: { xs: "100%", md: "50%" },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "24px",
          width: { xs: "90%", md: "360px" },
          mx: { xs: "auto", md: "0" },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography
            sx={{
              color: "var(--Gray-900, #101828)",
              fontFamily: "Montserrat",
              fontSize: "30px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "38px",
            }}
          >
            Information Details
          </Typography>
          <Typography
            sx={{
              color: "var(--Gray-600, #475467)",
              fontFamily: "Montserrat",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "24px",
            }}
          >
            Please Fill in the Required Information
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {fields.map((field, index) => {
            const key = field.title.toLowerCase().replace(/\s+/g, "_");
            return (
              <Box key={index} sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: "var(--Gray-700, #344054)",
                    fontFamily: "Montserrat",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "20px",
                  }}
                >
                  {field.default ? t(`pages.paymentLinks.defaultFields.${field.title}`) : field.title}{" "}
                  {field.required && "*"}
                </Typography>
                <TextField
                  fullWidth
                  placeholder={`Enter ${
                    field.default
                      ? t(`pages.paymentLinks.defaultFields.${field.title}`).toLocaleLowerCase()
                      : field.title.toLocaleLowerCase()
                  }`}
                  {...register(key)}
                  error={!!errors[key]}
                  helperText={errors[key]?.message?.toString()}
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "8px",
                      border: "1px solid var(--Gray-300, #D0D5DD)",
                    },
                  }}
                />
              </Box>
            );
          })}
        </Box>

        <Button
          variant="primary-md"
          sx={{
            borderRadius: "8px",
          }}
          type="submit"
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentLinkForm;
